import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Rocket, X, Send } from 'lucide-react';
import { useCMS } from '../hooks/useCMS';
import SafeHtml from '../components/SafeHtml';
import { getVisitorId } from '../lib/utils';
import { trackEvent } from '../lib/analytics';

// ============================================================
// 直播漏斗 bot（live-funnel 組件）——FB 直播流量的三幕轉化 widget
// State_1 迎賓＋身份分流 → State_2 痛點對比表 → State_3 稀缺 CTA
// 規則引擎（非 LLM）：禁跳幕／硬約束（150 字截斷／罐頭拒答）由代碼保證。
// 狀態存 localStorage（visitorId 鍵）——跨頁續攤，不落後端。
// 運費/國家問題查 shipping-zones 公開 API：有啟用區如實答，否則反幻覺罐頭。
// ============================================================

type Tag = 'B2B_Merchant' | 'B2C_Consumer';
type Act = 's1' | 's2' | 's3';

interface FunnelState {
  act: Act;
  tag: Tag | null;
}

interface Msg {
  role: 'bot' | 'user';
  text?: string;          // 純文字（受 150 字硬截斷）
  html?: string;          // 表格等結構內容（SafeHtml，不受字數限）
}

const PROSE_CAP = 150;

// 意圖關鍵詞（zh-HK 語境；命中任一即觸發）
const KW = {
  b2b: ['賣', '出海', '批發', '品牌', '商家', '出貨', '代理', '經銷', '入駐', 'b2b'],
  b2c: ['買', '訂', '購買', '食品', '貨品', '零食', '優惠', '商品', 'b2c', '想食'],
  advance: ['如何參與', '怎麼參與', '點參與', '如何購買', '怎麼買', '點買', '價格', '幾錢', '細節', '詳情', '落單', '想試', '報名', '預約'],
  shipping: ['運費', '幾耐', '時效', '郵費', '英國', '加拿大', '澳洲', '美國', 'au', 'uk', '寄', '清關', '關稅', '專線'],
};

const hit = (text: string, words: string[]) => words.some((w) => text.toLowerCase().includes(w.toLowerCase()));

export default function LiveFunnelBot() {
  const location = useLocation();
  const { getValue, getHtml } = useCMS();
  // 後台／供貨商門戶不顯示（widget 只服務前台訪客）
  const onBackoffice = /^\/(admin|supplier)(\/|$)/.test(location.pathname);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [funnel, setFunnel] = useState<FunnelState>({ act: 's1', tag: null });
  const [typing, setTyping] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  const storageKey = `live_funnel_${getVisitorId()}`;

  // 恢復狀態（跨頁續攤）
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as { funnel?: FunnelState; msgs?: Msg[] };
        if (parsed.funnel) setFunnel(parsed.funnel);
        if (parsed.msgs?.length) setMsgs(parsed.msgs);
      }
    } catch { /* ignore */ }
  }, [storageKey]);

  // /live?src=fblive 自動開場（FB 直播連結入口）
  useEffect(() => {
    if (started.current) return;
    const params = new URLSearchParams(window.location.search);
    if (location.pathname === '/live' && params.get('src')) {
      started.current = true;
      setTimeout(() => setOpen(true), 800);
    }
  }, [location.pathname]);

  const persist = useCallback((nextFunnel: FunnelState, nextMsgs: Msg[]) => {
    try { localStorage.setItem(storageKey, JSON.stringify({ funnel: nextFunnel, msgs: nextMsgs.slice(-40) })); } catch { /* ignore */ }
  }, [storageKey]);

  const say = useCallback((m: Msg[], nextFunnel: FunnelState) => {
    setTyping(true);
    setTimeout(() => {
      setMsgs((prev) => {
        const merged = [...prev, ...m];
        persist(nextFunnel, merged);
        return merged;
      });
      setFunnel(nextFunnel);
      setTyping(false);
    }, 450);
  }, [persist]);

  // ---------- 三幕台詞 ----------
  const greeting = getValue('live.greeting', '歡迎嚟到 YYC 直播間！🚀 剛剛直播介紹嘅香港好貨同出海方案，呢度幫你跟進。');
  const s1Buttons = {
    b2b: getValue('live.s1.btn_b2b', '我想把貨賣出海外 🚢'),
    b2c: getValue('live.s1.btn_b2c', '我想買香港貨 ✈️'),
  };
  const s1Reprompt = getValue('live.s1.reprompt', '先揀返你嘅身份，我先可以幫你最快著手 👇');
  const refusal = getValue('live.refusal', '呢個我答唔到喇～我專責幫你出海買賣香港貨，先揀返下面按鈕好嗎？');
  const noData = getValue('live.no_data', '此項具體細節將由您的專屬一對一顧問為您精確核算，請先點擊下方按鈕建立聯繫。');

  const s2 = {
    B2B_Merchant: {
      pain: getValue('live.s2.b2b.pain', '做品牌嘅你，最痛係咩？梗係貨出唔到去：'),
      table: getHtml('live.s2.b2b.table', '<table class="w-full text-[11px] leading-snug"><thead><tr class="text-left text-stone-500"><th class="pr-2">傳統出海</th><th class="text-x1493-primary">YYC 一站式</th></tr></thead><tbody class="align-top"><tr><td class="pr-2 py-1">清關自己摸路，敏感貨易扣</td><td class="py-1 font-bold">包稅專線一條龍✅</td></tr><tr><td class="pr-2 py-1">海外自建倉貴又慢</td><td class="py-1 font-bold">海外一件代發✅</td></tr><tr><td class="pr-2 py-1">逐個環節逐家傾</td><td class="py-1 font-bold">一個窗口搞掂✅</td></tr></tbody></table>'),
      probe: getValue('live.s2.b2b.probe', '您想主力出英國定加拿大市場？'),
    },
    B2C_Consumer: {
      pain: getValue('live.s2.b2c.pain', '買港貨最怕咩？怕慢、怕假貨：'),
      table: getHtml('live.s2.b2c.table', '<table class="w-full text-[11px] leading-snug"><thead><tr class="text-left text-stone-500"><th class="pr-2">一般代購集運</th><th class="text-x1493-primary">YYC 官方直郵</th></tr></thead><tbody class="align-top"><tr><td class="pr-2 py-1">集貨等單，隨時幾個禮拜</td><td class="py-1 font-bold">專線直郵，快速穩定✅</td></tr><tr><td class="pr-2 py-1">來源不明冇保證</td><td class="py-1 font-bold">香港門市直發正貨✅</td></tr><tr><td class="pr-2 py-1">人手報價各有不同</td><td class="py-1 font-bold">站上即時運費試算✅</td></tr></tbody></table>'),
      probe: getValue('live.s2.b2c.probe', '您想寄英國定加拿大？定想睇咩貨品？'),
    },
  };

  const s3 = {
    B2B_Merchant: {
      copy: getValue('live.s3.b2b.copy', '🔥 直播觀眾專屬：免費「出海方案診斷」名額有限，今場直播後截止！顧問一對一幫你計清航線、清關、代發成本。'),
      cta: getValue('live.cta.b2b', '預約免費出海方案診斷'),
      url: getValue('live.cta.b2b.url', '/b2b'),
    },
    B2C_Consumer: {
      copy: getValue('live.s3.b2c.copy', '⚡ 直播限定優惠限時開搶——名額倒數中！拎咗即刻結帳，YYC 直郵到你門口。'),
      cta: getValue('live.cta.b2c', '領取直播限定優惠並結帳'),
      url: getValue('live.cta.b2c.url', '/offers'),
    },
  };

  // ---------- AI 大腦（GLM；無 key／失敗回 null 由調用方跌返規則） ----------
  const askAI = useCallback(async (message: string, act: Act, tag: Tag | null): Promise<string | null> => {
    try {
      const res = await fetch('/api/public/live-funnel/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: message.slice(0, 300), act, tag }),
      });
      const j = await res.json();
      if (j?.mode === 'ai' && j.reply) return String(j.reply);
      return null;
    } catch {
      return null;
    }
  }, []);

  // ---------- 開場 ----------
  const startS1 = () => {
    if (msgs.length) return;
    say([{ text: greeting }], { act: 's1', tag: null });
  };

  useEffect(() => {
    if (open) startS1();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [msgs, typing]);

  const tagChosen = (tag: Tag) => {
    trackEvent('live_funnel_tag', { tag });
    const hook = tag === 'B2B_Merchant'
      ? getValue('live.s1.hook_b2b', '好嘢！搭上出海熱潮，香港品牌正喺海外爆發——等我同你拆解點行最快。')
      : getValue('live.s1.hook_b2c', '香港味道，海外都食到！等我話你知點樣最快到手。');
    say([{ text: hook }, { text: s2[tag].pain }, { html: s2[tag].table.__html }, { text: s2[tag].probe }], { act: 's2', tag });
  };

  const enterS3 = (tag: Tag) => {
    trackEvent('live_funnel_cta', { tag, act: 's3' });
    say([{ text: s3[tag].copy }], { act: 's3', tag });
  };

  // ---------- 運費問題：查 shipping-zones（反幻覺由資料源保證） ----------
  const answerShipping = async (): Promise<Msg> => {
    try {
      const res = await fetch('/api/public/shipping/zones');
      const json = await res.json();
      const zones: { name: string; code: string; rates: { label: string; etaDays: string | null }[] }[] = Array.isArray(json) ? json : json?.data || [];
      if (!zones.length) return { text: noData };
      const list = zones.map((z) => `${z.name}（${z.rates.map((r) => `${r.label}${r.etaDays ? ` 約${r.etaDays}日` : ''}`).join('/')}）`).join('、');
      return { text: getValue('live.shipping_zones_answer', `而家開通航線：{zones}。確實運費以結帳試算為準。`).replace('{zones}', list).slice(0, PROSE_CAP) };
    } catch {
      return { text: noData };
    }
  };

  const onSend = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    setMsgs((prev) => [...prev, { role: 'user', text }]);
    trackEvent('live_funnel_message', { act: funnel.act, tag: funnel.tag });

    if (funnel.act === 's1') {
      if (hit(text, KW.b2b)) { tagChosen('B2B_Merchant'); return; }
      if (hit(text, KW.b2c)) { tagChosen('B2C_Consumer'); return; }
      const ai1 = await askAI(text, 's1', null);
      say([{ text: ai1 ?? s1Reprompt }], funnel);
      return;
    }

    if (funnel.act === 's2' && funnel.tag) {
      if (hit(text, KW.advance)) { enterS3(funnel.tag); return; }
      if (hit(text, KW.shipping)) { const m = await answerShipping(); say([m, { text: s2[funnel.tag].probe }], funnel); return; }
      const ai = await askAI(text, 's2', funnel.tag);
      say([{ text: ai ?? refusal }], funnel);
      return;
    }

    if (funnel.act === 's3' && funnel.tag) {
      if (hit(text, KW.shipping)) { const m = await answerShipping(); say([m], funnel); return; }
      const ai3 = await askAI(text, 's3', funnel.tag);
      say([{ text: ai3 ?? s3[funnel.tag].copy }], funnel);
      return;
    }
  };

  const fabLabel = getValue('live.fab_label', '出海小助手');

  // 後台／供貨商門戶不渲染（hooks 之後，保持順序穩定）
  if (onBackoffice) return null;

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 flex items-center gap-2 bg-x1493-primary text-white pl-3 pr-4 py-3 rounded-full shadow-lg hover:brightness-110 transition"
          aria-label={fabLabel}
        >
          <Rocket size={20} className="text-x1493-secondary" />
          <span className="text-sm font-bold">{fabLabel}</span>
        </button>
      )}

      {open && (
        <div className="fixed bottom-5 right-5 z-40 w-[92vw] max-w-sm rounded-2xl shadow-2xl overflow-hidden flex flex-col bg-white border border-stone-200" style={{ height: 'min(560px, 72vh)' }}>
          <div className="bg-x1493-dark text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Rocket size={18} className="text-x1493-secondary" />
              <span className="font-bold text-sm">{getValue('live.bot_name', 'YYC 跨境出海星航員')}</span>
              {getValue('live.ai.badge', '') && (
                <span className="text-[9px] font-bold bg-x1493-secondary/25 text-x1493-secondary px-1.5 py-0.5 rounded">{getValue('live.ai.badge', '')}</span>
              )}
            </div>
            <button onClick={() => setOpen(false)} aria-label="關閉" className="p-1 hover:bg-white/10 rounded-full">
              <X size={16} />
            </button>
          </div>

          <div ref={bodyRef} className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-stone-50">
            {msgs.map((m, i) => (
              m.role === 'user' ? (
                <div key={i} className="flex justify-end">
                  <div className="bg-x1493-primary text-white text-[13px] leading-snug rounded-2xl rounded-br-md px-3 py-2 max-w-[85%]">{m.text}</div>
                </div>
              ) : (
                <div key={i} className="flex justify-start">
                  <div className="bg-white border border-stone-200 text-stone-800 text-[13px] leading-snug rounded-2xl rounded-bl-md px-3 py-2 max-w-[92%] [&_table]:my-1">
                    {m.html ? <SafeHtml html={m.html} /> : (m.text || '').slice(0, PROSE_CAP)}
                  </div>
                </div>
              )
            ))}

            {typing && (
              <div className="flex justify-start">
                <div className="bg-white border border-stone-200 rounded-2xl px-3 py-2 text-[13px] text-stone-400">…</div>
              </div>
            )}

            {/* 快捷回覆按鈕（依幕產生） */}
            {msgs.length > 0 && !typing && (
              <div className="flex flex-wrap gap-2 pt-1">
                {funnel.act === 's1' && (
                  <>
                    <button onClick={() => tagChosen('B2B_Merchant')} className="text-xs font-bold border-2 border-x1493-primary text-x1493-primary rounded-full px-3 py-1.5 hover:bg-x1493-primary hover:text-white transition">{s1Buttons.b2b}</button>
                    <button onClick={() => tagChosen('B2C_Consumer')} className="text-xs font-bold border-2 border-x1493-primary text-x1493-primary rounded-full px-3 py-1.5 hover:bg-x1493-primary hover:text-white transition">{s1Buttons.b2c}</button>
                  </>
                )}
                {funnel.act === 's2' && funnel.tag && (
                  <button onClick={() => enterS3(funnel.tag)} className="text-xs font-bold bg-x1493-secondary text-x1493-dark rounded-full px-3 py-1.5 hover:brightness-105 transition">
                    {getValue('live.s2.advance_btn', '我想參與 / 睇價格 →')}
                  </button>
                )}
                {funnel.act === 's3' && funnel.tag && (
                  <a href={s3[funnel.tag].url} onClick={() => trackEvent('live_funnel_cta_click', { tag: funnel.tag })} className="text-xs font-bold bg-x1493-primary text-white rounded-full px-4 py-2 hover:brightness-110 transition inline-flex items-center gap-1">
                    {s3[funnel.tag].cta} →
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="border-t p-2 flex gap-2 bg-white">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onSend(); }}
              placeholder={getValue('live.input_placeholder', '輸入你的問題…')}
              className="flex-1 border border-stone-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-x1493-primary/30"
              aria-label="聊天輸入"
            />
            <button onClick={onSend} className="bg-x1493-primary text-white rounded-xl px-3 hover:brightness-110 transition" aria-label="發送">
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
